import { locale } from "./locale.js";

/**
 * A decimal as it is written on a Swedish keyboard — read in, and written out.
 *
 * The comma is what the phone offers and what the back label prints — *9,5 g/l*,
 * *13,5 %* — and `Number("13,5")` is `NaN`, which travels as far as the server
 * before saying so. Read here rather than refused: the person typing it is
 * standing in front of a cabinet with a bottle in their hand, and the
 * difference between a comma and a point is not something to make them think
 * about.
 *
 * Lived privately in `screens/Gaps.tsx` until the alcohol field needed it too.
 * Moved rather than copied, which is the whole point: two copies of a rule
 * about what a keyboard offers is how one of the two fields ends up refusing
 * what the other accepts.
 */
export function decimalOf(text: string): number {
  return Number(text.replace(",", "."));
}

/**
 * Whether a typed decimal is one the field will accept, given its bounds.
 *
 * An empty field is sound: not saying is a valid answer everywhere on these
 * forms, and a blank that read as an error would make *I do not know* look
 * like a mistake.
 */
export function soundDecimal(text: string, min: number, max: number): boolean {
  if (text.trim() === "") return true;
  const value = decimalOf(text);
  return Number.isFinite(value) && value >= min && value <= max;
}

/**
 * The same comma, written back out.
 *
 * Presentation and not domain, for the reason `score.ts` gives about a score:
 * what the domain holds is the number, and how Sweden writes it down is the
 * screen's business. A whole number stays whole — *13 %*, not *13,0 %* —
 * because that is what the label says when the label says it.
 *
 * In the interface's language: a point in English, which `decimalOf` reads as
 * readily as the comma, so a value written out and typed back is the same.
 */
export function formatDecimal(value: number): string {
  return value.toLocaleString(locale(), { maximumFractionDigits: 2 });
}
